import axios from "axios";
import Vue from "vue";

export const TOKEN_STORAGE_KEY = "user-token";
export const TOKEN_REMEMBER_KEY = "remember-token";

/**
 * JSDoc for axios.CancelToken
 */
// eslint-disable-next-line
class CancelToken {
  /**
   *
   * @param {(cancelToken: Function) => void} executor
   */
  // eslint-disable-next-line
  constructor(executor) {}
}

export class SDK {
  constructor() {
    this._axios = axios.create({
      baseURL: "/api",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    /**
     * @type {Boolean}
     */
    this._rememberToken =
      window.localStorage.getItem(TOKEN_REMEMBER_KEY) === "true";

    /**
     * @type {String}
     */
    this._token = window.localStorage.getItem(TOKEN_STORAGE_KEY) || undefined;

    if (this._token) {
      this._axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${this._token}`;
    }

    this.eventBus = new Vue();
  }

  get token() {
    return this._token;
  }

  set token(token) {
    this._token = token;

    if (token) {
      this._axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${this._token}`;

      if (this.rememberToken) {
        window.localStorage.setItem(TOKEN_STORAGE_KEY, this._token);
      }
    } else {
      delete this._axios.defaults.headers.common["Authorization"];
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }

  get rememberToken() {
    return this._rememberToken;
  }

  set rememberToken(enable) {
    this._rememberToken = enable;
    window.localStorage.setItem(TOKEN_REMEMBER_KEY, this._rememberToken);
  }

  // #region Public Methods
  /**
   *
   * @param {Object} req
   * @param {String} req.email
   * @param {String} req.password
   */
  async logIn(req) {
    try {
      const res = await axios({
        method: "post",
        url: "/api/web-authenticate",
        data: req
      });

      this.token = res.data.access_token;

      return res;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      } else {
        throw error;
      }
    }
  }

  async logOut() {
    try {
      const res = await axios({
        url: "/api/logout",
        params: {
          token: this.token
        }
      });

      this.token = undefined;

      return res;
    } catch (error) {
      if (error.response) {
        throw error.response.data;
      } else {
        throw error;
      }
    }
  }

  /**
   * More detail <https://github.com/axios/axios>
   *
   * @param {Object} options
   * @param {String} options.url
   * @param {String} [options.method='get']
   * @param {Object|URLSearchParams} [options.params]
   * @param {Object|String|FormData|File|Blob|ArrayBuffer|ArrayBufferView|URLSearchParams} [options.data] Only 'PUT', 'POST', and 'PATCH'
   * @param {String} [options.baseURL]
   * @param {Object} [options.headers]
   * @param {Number} [options.timeout=0] default is `0` (no timeout)
   * @param {Boolean} [options.withCredentials=false]
   * @param {{ username: string, password: string }} [options.auth]
   * @param {String} [options.responseType='json'] options are 'arraybuffer', 'blob', 'document', 'json', 'text', 'stream'
   * @param {String} [options.responseEncoding='utf8']
   * @param {(progressEvent: ProgressEvent) => void} [options.onUploadProgress]
   * @param {(progressEvent: ProgressEvent) => void} [options.onDownloadProgress]
   * @param {Number} [options.maxContentLength=2000]
   * @param {{ host: string, port: number, auth: { username: string, password: string } }} [options.proxy]
   * @param {Array.<(data: Object, headers: Object) => String>} [options.transformRequest]
   * @param {Array.<(data: String | Object) => any>} [options.transformResponse]
   * @param {CancelToken} [options.cancelToken]
   *
   * @return {Promise<{ data: Object, status: Number, statusText: String, headers: Object, config: Object, request: Object }>} For more detail: <https://github.com/axios/axios#response-schema>
   */
  async request(options) {
    try {
      const res = await this._axios(options);
      return res;
    } catch (e) {
      if (this.isTokenExpired(e)) {
        await this.refreshToken();

        const res = await this.request(options);

        return res;
      } else if (this.isTokenInvalid(e)) {
        this.token = undefined;
        this.eventBus.$emit(
          "token-invalid",
          (e.response && e.response.data.message) || e.message
        );
        throw e;
      } else if (e.response) {
        throw e.response.data;
      } else {
        throw e;
      }
    }
  }

  validateToken() {
    return this.request({
      url: "/check"
    });
  }

  async refreshToken() {
    try {
      const res = await this._axios({
        url: "/token"
      });
      this.token = res.data.access_token;

      return res;
    } catch (e) {
      this.token = undefined;
      this.eventBus.$emit(
        "token-invalid",
        e.response.data.message || e.message
      );
      throw e;
    }
  }
  // #endregion

  /**
   *
   * @param {Error} e
   */
  isTokenExpired(e) {
    return e.response && e.response.data.code === "token_expired";
  }

  /**
   *
   * @param {Error} e
   */
  isTokenInvalid(e) {
    return (
      e.response &&
      (e.response.data.code === "token_invalid" ||
        e.response.data.code === "token_absent" ||
        e.response.data.message === "Unauthenticated.")
    );
  }
}
